import axios from 'axios';
import * as Sentry from '@sentry/react';
import { decodeJWT, clearJWT } from 'auth';
import config from './config';

// ================== Util ================== //

export function handleError(
  method,
  error,
  url,
  options = { redirectOnUnauthorized: true }
) {
  let response = {};

  if (error && error.source !== 'SW_BACKEND') {
    Sentry.captureException(error, {
      tags: {
        errorSource: 'Non-backend fetch error'
      }
    });
  }

  if (
    error &&
    error.response?.status === 401 &&
    options.redirectOnUnauthorized === true
  ) {
    clearJWT();
    window.location.reload();
  }

  if (error.response) {
    response = {
      statusCode: error.response.statusCode,
      type: error.response.type,
      ...error.response.data
    };
  }

  return Promise.reject(response);
}

export function createRequest({
  apiUrl = config.backendApiUrl,
  path,
  data,
  method,
  customHeaders = {},
  options,
  responseType
}) {
  let url = apiUrl + path;
  const accessToken = decodeJWT('accessToken');
  const userID = decodeJWT('userID');
  const companyID = decodeJWT('companyID');

  if (options && options.isNIOrchestrator) {
    url = config.niOrchestratorUrl + path;
  } else if (options && options.isReferral) {
    url = config.referralUrl + path;
  } else if (options && options.isAmbassador) {
    url = config.ambassadorUrl + path;
  } else if (options && options.isUserService) {
    url = config.userServiceUrl + path;
  }

  const headers = {
    'Content-Type': 'application/json',
    ...customHeaders
  };

  if (accessToken) {
    headers.accessToken = accessToken;
    headers.authorization = `Bearer ${accessToken}`;
  }

  if (userID) {
    headers.userID = userID;
  }

  if (companyID) {
    headers.companyID = companyID;
  }

  return {
    url,
    headers,
    method,
    data,
    responseType
  };
}

// ================== Public ================== //

const getNoCacheUrl = (url) => {
  if (url.includes('?')) {
    return `${url}&nocache=${new Date().getTime()}`;
  }
  return `${url}?nocache=${new Date().getTime()}`;
};

export function get(
  url,
  options = {
    redirectOnUnauthorized: true,
    isNIOrchestrator: false,
    isUserService: false
  },
  customHeaders,
  nocache = false
) {
  const request = createRequest({
    path: nocache ? getNoCacheUrl(url) : url,
    method: 'get',
    customHeaders,
    options
  });
  return axios(request)
    .then((response) => response.data)
    .catch((error) => handleError('GET', error, url, options));
}

export function post(
  url,
  data = null,
  options = {
    redirectOnUnauthorized: true,
    isNIOrchestrator: false,
    isUserService: false
  },
  customHeaders,
  responseType = 'application/json'
) {
  return axios(
    createRequest({
      path: url,
      data,
      method: 'POST',
      customHeaders,
      options,
      responseType
    })
  )
    .then((response) => response.data)
    .catch((error) => handleError('POST', error, url, options));
}

export function postMultipart(
  url,
  data = {},
  options = { redirectOnUnauthorized: true, isNIOrchestrator: false }
) {
  const body = Object.keys(data).reduce((f, key) => {
    f.append(key, data[key]);
    return f;
  }, new FormData());
  return axios(createRequest({ path: url, data: body, method: 'POST', options }))
    .then((response) => response.data)
    .catch((error) => handleError('POST_MULTIPART', error, url));
}

export function put(
  url,
  data = null,
  options = { redirectOnUnauthorized: true, isNIOrchestrator: false }
) {
  return axios(createRequest({ path: url, data, method: 'PUT', options }))
    .then((response) => response.data)
    .catch((error) => handleError('PUT', error, url));
}

export function patch(
  url,
  data = null,
  options = { redirectOnUnauthorized: true, isNIOrchestrator: false }
) {
  return axios(createRequest({ path: url, data, method: 'PATCH', options }))
    .then((response) => response.data)
    .catch((error) => handleError('PATCH', error, url));
}

export function del(
  url,
  data = null,
  options = { redirectOnUnauthorized: true, isNIOrchestrator: false }
) {
  return axios(createRequest({ path: url, data, method: 'DELETE', options }))
    .then((response) => response.data)
    .catch((error) => handleError('DELETE', error, url));
}
